import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../App.css";
import { supabase } from "../supabaseClient";
import clearbadgeLogoColor from "../assets/clearbadge-logo-color.png";
import {
  TRUST_SESSION_SMS_CONSENT_CONFIRMATION_LABEL,
  TRUST_SESSION_SMS_VERBAL_CONSENT_SCRIPT,
} from "../constants/trustSessionSms";

const API_BASE = process.env.REACT_APP_API_URL || "";
const TRUST_SESSION_SMS_ENABLED =
  String(process.env.REACT_APP_TRUST_SESSION_SMS_ENABLED || "").trim().toLowerCase() === "true";
const FALLBACK_ICE_SERVERS = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];
const TRUST_HOST_SIGNAL_FAST_POLL_MS = 180;
const TRUST_HOST_SIGNAL_IDLE_POLL_MS = 900;
const TRUST_HOST_SIGNAL_ACTIVE_WINDOW_MS = 3000;
const TRUST_HOST_RECONNECT_GRACE_MS = 300000;
const TRUST_HOST_ICE_RESTART_DEBOUNCE_MS = 3000;
const DEFAULT_TRUST_SESSION_LIMITS = Object.freeze({
  maxDurationMinutes: 60,
  maxResolutionWidth: 1280,
  maxResolutionHeight: 720,
  maxVideoBitrate: 2_500_000,
  maxFramerate: 30,
});
const buildVideoCaptureConstraints = ({ maxResolutionWidth, maxResolutionHeight, maxFramerate }) => ({
  video: {
    width: { ideal: maxResolutionWidth, max: maxResolutionWidth },
    height: { ideal: maxResolutionHeight, max: maxResolutionHeight },
    frameRate: { ideal: maxFramerate, max: maxFramerate },
  },
  audio: false,
});

const makeTrustConnectionId = (prefix) => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
};

const applySenderQuality = async (sender, { maxBitrate, maxFramerate } = {}) => {
  if (!sender || sender.track?.kind !== "video") return;
  const base = sender.getParameters ? sender.getParameters() : null;
  if (!base) return;
  const params = { ...base };
  const encodings = Array.isArray(params.encodings) && params.encodings.length > 0 ? params.encodings : [{}];
  params.encodings = encodings.map((encoding, idx) =>
    idx === 0
      ? {
        ...encoding,
        ...(Number.isFinite(maxBitrate) ? { maxBitrate } : null),
        ...(Number.isFinite(maxFramerate) ? { maxFramerate } : null),
      }
      : encoding
  );

  try {
    await sender.setParameters(params);
  } catch (_err) {
    // Some browsers are strict about sender params; ignore if unsupported.
  }
};

const resolveHostIceServers = async ({ sessionToken, accessToken }) => {
  if (!sessionToken || !accessToken) return FALLBACK_ICE_SERVERS;
  try {
    const res = await fetch(
      `${API_BASE}/api/trust-session/${encodeURIComponent(sessionToken)}/ice-servers?role=host`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        cache: "no-store",
      }
    );
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) return FALLBACK_ICE_SERVERS;
    const iceServers = Array.isArray(payload?.iceServers) ? payload.iceServers : [];
    return iceServers.length > 0 ? iceServers : FALLBACK_ICE_SERVERS;
  } catch (_err) {
    return FALLBACK_ICE_SERVERS;
  }
};

const waitForVideoDimensions = async (videoEl, { timeoutMs = 1500 } = {}) => {
  const hasDimensions = () => (Number(videoEl?.videoWidth) || 0) > 0 && (Number(videoEl?.videoHeight) || 0) > 0;
  if (hasDimensions()) return;

  await new Promise((resolve) => {
    let settled = false;
    let timeoutId = null;
    let rafId = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      videoEl?.removeEventListener?.("loadedmetadata", check);
      videoEl?.removeEventListener?.("resize", check);
      resolve();
    };

    const check = () => {
      if (hasDimensions()) {
        finish();
        return;
      }
      rafId = requestAnimationFrame(check);
    };

    timeoutId = setTimeout(finish, timeoutMs);
    videoEl?.addEventListener?.("loadedmetadata", check);
    videoEl?.addEventListener?.("resize", check);
    check();
  });
};

const fitWithinLimits = ({ sourceWidth, sourceHeight, maxWidth, maxHeight }) => {
  const width = Number(sourceWidth) || 0;
  const height = Number(sourceHeight) || 0;
  if (width <= 0 || height <= 0) {
    return { width: maxWidth, height: maxHeight };
  }
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

export default function TrustSessionHostPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [, setNotice] = useState("");
  const [isLive, setIsLive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [sendingSms, setSendingSms] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showSmsConsentModal, setShowSmsConsentModal] = useState(false);
  const [smsRecipient, setSmsRecipient] = useState("");
  const [smsConsentConfirmed, setSmsConsentConfirmed] = useState(false);
  const [smsModalStatus, setSmsModalStatus] = useState("idle");
  const [smsModalMessage, setSmsModalMessage] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailModalStatus, setEmailModalStatus] = useState("idle");
  const [emailModalMessage, setEmailModalMessage] = useState("");
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareScreenEnabled, setShareScreenEnabled] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [sessionCodeEnabled, setSessionCodeEnabled] = useState(false);
  const [liveScreenEnabled, setLiveScreenEnabled] = useState(false);
  const [liveCameraEnabled, setLiveCameraEnabled] = useState(true);
  const [screenPaused, setScreenPaused] = useState(false);
  const [screenPrompting, setScreenPrompting] = useState(false);
  const [viewerTelemetry, setViewerTelemetry] = useState(null);
  const [viewerPointer, setViewerPointer] = useState(null);
  const previewRef = useRef(null);
  const peerRef = useRef(null);
  const pollRef = useRef(null);
  const cursorRef = useRef(0);
  const screenStreamRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const composedStreamRef = useRef(null);
  const screenTrackRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const screenVideoElRef = useRef(null);
  const cameraVideoElRef = useRef(null);
  const frozenScreenFrameRef = useRef(null);
  const manualScreenStopRef = useRef(false);
  const screenPausedRef = useRef(false);
  const rafRef = useRef(null);
  const pendingRemoteCandidatesRef = useRef([]);
  const reconnectGraceTimerRef = useRef(null);
  const smsModalTimerRef = useRef(null);
  const emailModalTimerRef = useRef(null);
  const trustPlaceholderLogoRef = useRef(null);
  const connectionEpochRef = useRef("");
  const activeOfferIdRef = useRef("");

  const trustSessionLimits = useMemo(() => {
    const maxDurationMinutesRaw = Number(session?.max_duration_minutes);
    const maxResolutionHeightRaw = Number(session?.max_resolution_height);
    const maxResolutionWidthRaw = Number(session?.max_resolution_width);
    const maxVideoBitrateRaw = Number(session?.max_video_bitrate);
    const maxFramerateRaw = Number(session?.max_framerate);

    const maxDurationMinutes =
      Number.isFinite(maxDurationMinutesRaw) && maxDurationMinutesRaw > 0
        ? Math.round(maxDurationMinutesRaw)
        : DEFAULT_TRUST_SESSION_LIMITS.maxDurationMinutes;
    const maxResolutionHeight =
      Number.isFinite(maxResolutionHeightRaw) && maxResolutionHeightRaw > 0
        ? Math.round(maxResolutionHeightRaw)
        : DEFAULT_TRUST_SESSION_LIMITS.maxResolutionHeight;
    const maxResolutionWidth =
      Number.isFinite(maxResolutionWidthRaw) && maxResolutionWidthRaw > 0
        ? Math.round(maxResolutionWidthRaw)
        : Math.max(640, Math.round((maxResolutionHeight * 16) / 9));
    const maxVideoBitrate =
      Number.isFinite(maxVideoBitrateRaw) && maxVideoBitrateRaw > 0
        ? Math.round(maxVideoBitrateRaw)
        : (maxResolutionHeight >= 1080 ? 5_000_000 : DEFAULT_TRUST_SESSION_LIMITS.maxVideoBitrate);
    const maxFramerate =
      Number.isFinite(maxFramerateRaw) && maxFramerateRaw > 0
        ? Math.round(maxFramerateRaw)
        : DEFAULT_TRUST_SESSION_LIMITS.maxFramerate;

    return {
      maxDurationMinutes,
      maxResolutionWidth,
      maxResolutionHeight,
      maxVideoBitrate,
      maxFramerate,
    };
  }, [session?.max_duration_minutes, session?.max_resolution_height, session?.max_resolution_width, session?.max_video_bitrate, session?.max_framerate]);

  const videoCaptureConstraints = useMemo(
    () =>
      buildVideoCaptureConstraints({
        maxResolutionWidth: trustSessionLimits.maxResolutionWidth,
        maxResolutionHeight: trustSessionLimits.maxResolutionHeight,
        maxFramerate: trustSessionLimits.maxFramerate,
      }),
    [trustSessionLimits.maxFramerate, trustSessionLimits.maxResolutionHeight, trustSessionLimits.maxResolutionWidth]
  );
  const viewerVisibleFrame = useMemo(() => {
    const raw = viewerTelemetry?.visibleFrame || viewerTelemetry?.windowFrame;
    const clamp = (value) => {
      if (!Number.isFinite(value)) return 0;
      return Math.max(0, Math.min(1, value));
    };
    const x = clamp(raw?.x);
    const y = clamp(raw?.y);
    const width = clamp(raw?.width);
    const height = clamp(raw?.height);
    return {
      x,
      y,
      width: Math.min(width, 1 - x),
      height: Math.min(height, 1 - y),
    };
  }, [viewerTelemetry]);

  const viewerHasVisibleFrame = viewerVisibleFrame.width > 0.01 && viewerVisibleFrame.height > 0.01;
  const showViewerActivityOverlay = isLive && viewerCount > 0;
  const viewerTelemetryReady = !!viewerTelemetry;
  const viewerAwayFromSession = viewerTelemetryReady && viewerTelemetry.active === false;
  const viewerPointerFrame = useMemo(() => {
    if (!viewerPointer?.active) return null;
    const x = Number(viewerPointer.x);
    const y = Number(viewerPointer.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return {
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
      kind: String(viewerPointer.kind || "move").toLowerCase(),
      pointerType: String(viewerPointer.pointerType || "mouse").toLowerCase(),
      sequence: viewerPointer.sequence || viewerPointer.updatedAt || 0,
    };
  }, [viewerPointer]);
  const showViewerPointer = showViewerActivityOverlay && !viewerAwayFromSession && !!viewerPointerFrame;
  const showViewerPointerPulse =
    showViewerPointer && (viewerPointerFrame.kind === "click" || viewerPointerFrame.kind === "tap");


  const getAccessToken = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token || "";
  }, []);

  const clearScreenSourceRefs = useCallback(({ preserveFrozenFrame = false } = {}) => {
    if (screenVideoElRef.current) {
      screenVideoElRef.current.srcObject = null;
      screenVideoElRef.current = null;
    }
    screenStreamRef.current = null;
    screenTrackRef.current = null;
    if (!preserveFrozenFrame) {
      frozenScreenFrameRef.current = null;
    }
  }, []);

  const clearCameraSourceRefs = useCallback(() => {
    if (cameraVideoElRef.current) {
      cameraVideoElRef.current.srcObject = null;
      cameraVideoElRef.current = null;
    }
    cameraStreamRef.current = null;
    cameraTrackRef.current = null;
  }, []);

  const captureFrozenScreenFrame = useCallback(() => {
    const screenVideo = screenVideoElRef.current;
    if (!screenVideo) return false;
    const videoWidth = Number(screenVideo.videoWidth) || 0;
    const videoHeight = Number(screenVideo.videoHeight) || 0;
    if (videoWidth <= 0 || videoHeight <= 0) return false;

    const frameCanvas = document.createElement("canvas");
    frameCanvas.width = videoWidth;
    frameCanvas.height = videoHeight;
    const frameCtx = frameCanvas.getContext("2d");
    if (!frameCtx) return false;
    frameCtx.drawImage(screenVideo, 0, 0, videoWidth, videoHeight);
    frozenScreenFrameRef.current = frameCanvas;
    return true;
  }, []);

  const stopMediaStreams = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const stopStream = (streamRef) => {
      if (!streamRef.current) return;
      for (const track of streamRef.current.getTracks()) {
        try {
          track.stop();
        } catch (_err) {
          // ignore
        }
      }
      streamRef.current = null;
    };
    manualScreenStopRef.current = true;
    stopStream(screenStreamRef);
    stopStream(cameraStreamRef);
    stopStream(composedStreamRef);
    clearScreenSourceRefs();
    clearCameraSourceRefs();
    screenPausedRef.current = false;
    setScreenPaused(false);
    setScreenPrompting(false);
    manualScreenStopRef.current = false;
    if (previewRef.current) previewRef.current.srcObject = null;
  }, [clearCameraSourceRefs, clearScreenSourceRefs]);

  const clearRealtime = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (reconnectGraceTimerRef.current) {
      clearTimeout(reconnectGraceTimerRef.current);
      reconnectGraceTimerRef.current = null;
    }
    if (peerRef.current) {
      try {
        peerRef.current.onicecandidate = null;
        peerRef.current.onconnectionstatechange = null;
        peerRef.current.close();
      } catch (_err) {
        // ignore
      }
      peerRef.current = null;
    }
    pendingRemoteCandidatesRef.current = [];
    connectionEpochRef.current = "";
    activeOfferIdRef.current = "";
    stopMediaStreams();
    cursorRef.current = 0;
    setViewerTelemetry(null);
    setViewerPointer(null);
    setIsLive(false);
    setViewerCount(0);
    setShowPreview(false);
    setPreviewLoading(false);
  }, [stopMediaStreams]);

  useEffect(() => () => clearRealtime(), [clearRealtime]);
  useEffect(() => {
    const image = new Image();
    image.src = clearbadgeLogoColor;
    trustPlaceholderLogoRef.current = image;
    return () => {
      trustPlaceholderLogoRef.current = null;
    };
  }, []);
  useEffect(
    () => () => {
      if (smsModalTimerRef.current) {
        clearTimeout(smsModalTimerRef.current);
        smsModalTimerRef.current = null;
      }
      if (emailModalTimerRef.current) {
        clearTimeout(emailModalTimerRef.current);
        emailModalTimerRef.current = null;
      }
    },
    []
  );

  const loadSession = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError("Please sign in again.");
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_BASE}/api/trust-sessions/${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(payload?.error || "Unable to load trust session.");
        setLoading(false);
        return;
      }
      const nextSession = payload?.session || null;
      setSession(nextSession);
      if (nextSession) {
        const nextShareScreenEnabled = nextSession.include_screen !== false;
        const nextCameraEnabled = !!nextSession.include_camera;
        setShareScreenEnabled(nextShareScreenEnabled);
        setCameraEnabled(nextCameraEnabled);
        setSessionCodeEnabled(nextSession.session_code_enabled !== false);
        setLiveScreenEnabled(nextShareScreenEnabled);
        setLiveCameraEnabled(nextCameraEnabled);
      }
    } catch (_err) {
      setError("Unable to load trust session.");
    } finally {
      setLoading(false);
    }
  }, [id, getAccessToken]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const copyLink = async () => {
    if (!session?.session_url) return;
    try {
      await navigator.clipboard.writeText(session.session_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_err) {
      // ignore
    }
  };

  const openSmsModal = () => {
    if (!TRUST_SESSION_SMS_ENABLED) return;
    if (!session?.id || !isLive) return;
    if (smsModalTimerRef.current) {
      clearTimeout(smsModalTimerRef.current);
      smsModalTimerRef.current = null;
    }
    setSmsRecipient("");
    setSmsConsentConfirmed(false);
    setShowSmsConsentModal(false);
    setSmsModalStatus("idle");
    setSmsModalMessage("");
    setShowSmsModal(true);
  };

  const closeSmsModal = () => {
    if (sendingSms) return;
    if (smsModalTimerRef.current) {
      clearTimeout(smsModalTimerRef.current);
      smsModalTimerRef.current = null;
    }
    setShowSmsModal(false);
    setShowSmsConsentModal(false);
    setSmsConsentConfirmed(false);
    setSmsModalStatus("idle");
    setSmsModalMessage("");
  };

  const continueToSmsConsent = (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!TRUST_SESSION_SMS_ENABLED) return;
    if (!session?.id || !isLive || sendingSms) return;
    const to = String(smsRecipient || "").trim();
    if (!to) {
      setSmsModalStatus("error");
      setSmsModalMessage("Phone number is required.");
      return;
    }
    setSmsModalStatus("idle");
    setSmsModalMessage("");
    setSmsConsentConfirmed(false);
    setShowSmsModal(false);
    setShowSmsConsentModal(true);
  };

  const sendSmsLink = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!TRUST_SESSION_SMS_ENABLED) return;
    if (!session?.id || !isLive || sendingSms) return;
    const to = String(smsRecipient || "").trim();
    if (!to) {
      setSmsModalStatus("error");
      setSmsModalMessage("Phone number is required.");
      return;
    }
    if (!smsConsentConfirmed) {
      setSmsModalStatus("error");
      setSmsModalMessage("Confirm verbal consent before sending the SMS.");
      return;
    }

    setSendingSms(true);
    setSmsModalStatus("idle");
    setSmsModalMessage("");
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setSmsModalStatus("error");
        setSmsModalMessage("Please sign in again.");
        return;
      }
      const res = await fetch(`${API_BASE}/api/trust-session/send-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          sessionId: session.id,
          to,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSmsModalStatus("error");
        setSmsModalMessage(payload?.error || "Unable to send SMS link.");
        return;
      }
      setSmsModalStatus("success");
      setSmsModalMessage("SMS sent.");
      setNotice(`SMS sent to ${payload?.to || to}. Session ID: ${payload?.sessionShortId || "N/A"}.`);
      smsModalTimerRef.current = setTimeout(() => {
        setShowSmsModal(false);
        setShowSmsConsentModal(false);
        setSmsModalStatus("idle");
        setSmsModalMessage("");
      }, 900);
    } catch (_err) {
      setSmsModalStatus("error");
      setSmsModalMessage("Unable to send SMS link.");
    } finally {
      setSendingSms(false);
    }
  };

  const openEmailModal = () => {
    if (!session?.id || !isLive) return;
    if (emailModalTimerRef.current) {
      clearTimeout(emailModalTimerRef.current);
      emailModalTimerRef.current = null;
    }
    setEmailRecipient("");
    setEmailModalStatus("idle");
    setEmailModalMessage("");
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    if (sendingEmail) return;
    if (emailModalTimerRef.current) {
      clearTimeout(emailModalTimerRef.current);
      emailModalTimerRef.current = null;
    }
    setShowEmailModal(false);
    setEmailModalStatus("idle");
    setEmailModalMessage("");
  };

  const sendEmailLink = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!session?.id || !isLive || sendingEmail) return;
    const to = String(emailRecipient || "").trim();
    if (!to) {
      setEmailModalStatus("error");
      setEmailModalMessage("Email address is required.");
      return;
    }

    setSendingEmail(true);
    setEmailModalStatus("idle");
    setEmailModalMessage("");
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setEmailModalStatus("error");
        setEmailModalMessage("Please sign in again.");
        return;
      }
      const res = await fetch(`${API_BASE}/api/trust-session/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          sessionId: session.id,
          to,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setEmailModalStatus("error");
        setEmailModalMessage(payload?.error || "Unable to send email link.");
        return;
      }
      setEmailModalStatus("success");
      setEmailModalMessage("Email sent.");
      setNotice(`Email sent to ${payload?.to || to}. Session ID: ${payload?.sessionShortId || "N/A"}.`);
      emailModalTimerRef.current = setTimeout(() => {
        setShowEmailModal(false);
        setEmailModalStatus("idle");
        setEmailModalMessage("");
      }, 900);
    } catch (_err) {
      setEmailModalStatus("error");
      setEmailModalMessage("Unable to send email link.");
    } finally {
      setSendingEmail(false);
    }
  };

  const sendHostSignal = async (accessToken, signalType, payload) => {
    await fetch(`${API_BASE}/api/trust-session/${encodeURIComponent(session.token)}/signal`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        senderRole: "host",
        signalType,
        payload: {
          ...payload,
          connectionEpoch: payload?.connectionEpoch || connectionEpochRef.current || "",
          offerId: payload?.offerId || activeOfferIdRef.current || "",
        },
      }),
    });
  };

  const requestScreenSource = useCallback(async () => {
    const captureController =
      typeof window !== "undefined" && typeof window.CaptureController === "function"
        ? new window.CaptureController()
        : null;
    const displayMediaOptions = captureController
      ? { ...videoCaptureConstraints, controller: captureController }
      : videoCaptureConstraints;
    const screenStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
    const nextTrack = screenStream.getVideoTracks()[0] || null;
    if (!nextTrack) {
      for (const track of screenStream.getTracks()) {
        try {
          track.stop();
        } catch (_err) {
          // ignore
        }
      }
      throw new Error("Unable to capture screen source.");
    }

    const displaySurface = String(nextTrack.getSettings?.().displaySurface || "").toLowerCase();
    if (captureController && (displaySurface === "browser" || displaySurface === "window")) {
      try {
        captureController.setFocusBehavior("no-focus-change");
      } catch (_err) {
        // Ignore unsupported or late focus-behavior updates.
      }
    }

    const screenVideo = document.createElement("video");
    screenVideo.srcObject = screenStream;
    screenVideo.playsInline = true;
    screenVideo.muted = true;
    await screenVideo.play();

    try {
      await nextTrack.applyConstraints({
        width: { max: trustSessionLimits.maxResolutionWidth },
        height: { max: trustSessionLimits.maxResolutionHeight },
        frameRate: { max: trustSessionLimits.maxFramerate },
      });
    } catch (_err) {
      // Browsers may reject display track constraints; continue with captured stream.
    }

    await waitForVideoDimensions(screenVideo);

    nextTrack.contentHint = "detail";
    return { screenStream, nextTrack, screenVideo };
  }, [trustSessionLimits.maxFramerate, trustSessionLimits.maxResolutionHeight, trustSessionLimits.maxResolutionWidth, videoCaptureConstraints]);

  const ensureScreenSource = useCallback(async ({ forcePrompt = false, keepPausedState = false, keepFrozenFrame = false } = {}) => {
    const existingTrack = screenTrackRef.current;
    if (!forcePrompt && existingTrack && existingTrack.readyState === "live" && screenVideoElRef.current) {
      return existingTrack;
    }

    const previousStream = screenStreamRef.current;
    const previousTrack = screenTrackRef.current;
    const previousVideo = screenVideoElRef.current;
    const { screenStream, nextTrack, screenVideo } = await requestScreenSource();

    if (previousTrack) {
      previousTrack.onended = null;
    }
    if (previousVideo) {
      previousVideo.srcObject = null;
    }

    screenStreamRef.current = screenStream;
    screenTrackRef.current = nextTrack;
    screenVideoElRef.current = screenVideo;
    if (!keepFrozenFrame) {
      frozenScreenFrameRef.current = null;
    }
    if (!keepPausedState) {
      screenPausedRef.current = false;
      setScreenPaused(false);
    }

    if (previousStream) {
      for (const track of previousStream.getTracks()) {
        try {
          track.stop();
        } catch (_err) {
          // ignore
        }
      }
    }

    setLiveScreenEnabled(true);
    return nextTrack;
  }, [requestScreenSource]);

  const ensureCameraSource = useCallback(async () => {
    const existingTrack = cameraTrackRef.current;
    if (existingTrack && existingTrack.readyState === "live" && cameraVideoElRef.current) {
      return existingTrack;
    }

    const cameraStream = await navigator.mediaDevices.getUserMedia(videoCaptureConstraints);
    const nextTrack = cameraStream.getVideoTracks()[0] || null;
    if (!nextTrack) {
      for (const track of cameraStream.getTracks()) {
        try {
          track.stop();
        } catch (_err) {
          // ignore
        }
      }
      throw new Error("Unable to capture camera source.");
    }

    const cameraVideo = document.createElement("video");
    cameraVideo.srcObject = cameraStream;
    cameraVideo.playsInline = true;
    cameraVideo.muted = true;
    await cameraVideo.play();

    cameraStreamRef.current = cameraStream;
    cameraTrackRef.current = nextTrack;
    cameraVideoElRef.current = cameraVideo;
    nextTrack.contentHint = "motion";
    setLiveCameraEnabled(nextTrack.enabled !== false);
    return nextTrack;
  }, [videoCaptureConstraints]);

  const bindLiveScreenEndedHandler = useCallback((track) => {
    if (!track) return;
    track.onended = () => {
      if (manualScreenStopRef.current) {
        manualScreenStopRef.current = false;
        return;
      }
      clearScreenSourceRefs({ preserveFrozenFrame: false });
      setLiveScreenEnabled(false);
      setShareScreenEnabled(false);
      screenPausedRef.current = false;
      setScreenPaused(false);
      setNotice("Screen share stopped. Turn Screen share live on to choose a new window.");
    };
  }, [clearScreenSourceRefs]);

  const createOutputStream = async ({ useScreen, useCamera }) => {
    if (!useScreen && !useCamera) {
      throw new Error("Select at least one source.");
    }

    if (useScreen) {
      await ensureScreenSource();
    }
    if (useCamera) {
      await ensureCameraSource();
    }

    const resolveCanvasSize = () => {
      const activeScreenVideo = screenVideoElRef.current;
      const activeScreenTrack = screenTrackRef.current;
      const activeCameraVideo = cameraVideoElRef.current;
      const screenWidth = Number(activeScreenVideo?.videoWidth) || Number(activeScreenTrack?.getSettings?.().width) || 0;
      const screenHeight = Number(activeScreenVideo?.videoHeight) || Number(activeScreenTrack?.getSettings?.().height) || 0;
      if (screenWidth > 0 && screenHeight > 0) {
        return fitWithinLimits({
          sourceWidth: screenWidth,
          sourceHeight: screenHeight,
          maxWidth: trustSessionLimits.maxResolutionWidth,
          maxHeight: trustSessionLimits.maxResolutionHeight,
        });
      }

      const cameraWidth = Number(activeCameraVideo?.videoWidth) || 0;
      const cameraHeight = Number(activeCameraVideo?.videoHeight) || 0;
      if (cameraWidth > 0 && cameraHeight > 0) {
        return fitWithinLimits({
          sourceWidth: cameraWidth,
          sourceHeight: cameraHeight,
          maxWidth: trustSessionLimits.maxResolutionWidth,
          maxHeight: trustSessionLimits.maxResolutionHeight,
        });
      }

      return {
        width: trustSessionLimits.maxResolutionWidth,
        height: trustSessionLimits.maxResolutionHeight,
      };
    };

    let { width, height } = resolveCanvasSize();
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return screenStreamRef.current;

    const drawFrame = () => {
      const nextCanvasSize = resolveCanvasSize();
      if (canvas.width !== nextCanvasSize.width || canvas.height !== nextCanvasSize.height) {
        canvas.width = nextCanvasSize.width;
        canvas.height = nextCanvasSize.height;
      }
      width = canvas.width;
      height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      const activeScreenTrack = screenTrackRef.current;
      const activeScreenVideo = screenVideoElRef.current;
      const hasActiveScreenFrame =
        !!activeScreenTrack &&
        activeScreenTrack.readyState === "live" &&
        activeScreenTrack.enabled !== false &&
        !screenPausedRef.current &&
        !!activeScreenVideo;
      const hasFrozenScreenFrame = !!frozenScreenFrameRef.current;
      const activeCameraTrack = cameraTrackRef.current;
      const activeCameraVideo = cameraVideoElRef.current;
      const hasActiveCameraFrame =
        !!activeCameraTrack &&
        activeCameraTrack.readyState === "live" &&
        activeCameraTrack.enabled !== false &&
        !!activeCameraVideo;

      const drawVideoCover = (video, targetWidth, targetHeight) => {
        const videoWidth = Number(video.videoWidth) || targetWidth;
        const videoHeight = Number(video.videoHeight) || targetHeight;
        if (videoWidth <= 0 || videoHeight <= 0) {
          ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
          return;
        }
        const videoAspect = videoWidth / videoHeight;
        const targetAspect = targetWidth / targetHeight;
        let sx = 0;
        let sy = 0;
        let sw = videoWidth;
        let sh = videoHeight;
        if (videoAspect > targetAspect) {
          sw = Math.max(1, Math.round(videoHeight * targetAspect));
          sx = Math.max(0, Math.round((videoWidth - sw) / 2));
        } else if (videoAspect < targetAspect) {
          sh = Math.max(1, Math.round(videoWidth / targetAspect));
          sy = Math.max(0, Math.round((videoHeight - sh) / 2));
        }
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
      };

      if (
        hasActiveScreenFrame
      ) {
        ctx.drawImage(activeScreenVideo, 0, 0, width, height);
      } else if (hasFrozenScreenFrame) {
        ctx.drawImage(frozenScreenFrameRef.current, 0, 0, width, height);
      } else if (hasActiveCameraFrame) {
        drawVideoCover(activeCameraVideo, width, height);
      } else {
        ctx.fillStyle = "#0b1220";
        ctx.fillRect(0, 0, width, height);

        const title = "Trust Session";
        const logoImage = trustPlaceholderLogoRef.current;
        const titleSize = Math.max(26, Math.round(width * 0.045));
        const gap = Math.max(12, Math.round(height * 0.018));
        let logoHeight = 0;
        let logoWidth = 0;

        if (logoImage && logoImage.complete && logoImage.naturalWidth > 0 && logoImage.naturalHeight > 0) {
          const logoAspect = logoImage.naturalWidth / logoImage.naturalHeight;
          logoWidth = Math.min(Math.round(width * 0.34), 360);
          logoHeight = Math.round(logoWidth / logoAspect);
        }

        const totalHeight = logoHeight > 0 ? logoHeight + gap + titleSize : titleSize;
        let blockTop = Math.round((height - totalHeight) / 2);
        if (logoHeight > 0) {
          const logoX = Math.round((width - logoWidth) / 2);
          const logoY = blockTop;
          ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
          blockTop += logoHeight + gap;
        }

        ctx.fillStyle = "#dbe8ff";
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.font = `700 ${titleSize}px Inter, Arial, sans-serif`;
        ctx.fillText(title, Math.round(width / 2), blockTop + titleSize);
        ctx.textAlign = "start";
      }

      if (
        hasActiveCameraFrame &&
        (hasActiveScreenFrame || hasFrozenScreenFrame)
      ) {
        const pipWidth = Math.round(width * 0.22);
        const pipHeight = Math.round((pipWidth * 9) / 16);
        const padding = Math.max(12, Math.round(width * 0.012));
        const pipX = width - pipWidth - padding;
        const pipY = height - pipHeight - padding;
        ctx.fillStyle = "rgba(15, 23, 42, 0.55)";
        ctx.fillRect(pipX - 4, pipY - 4, pipWidth + 8, pipHeight + 8);
        ctx.drawImage(activeCameraVideo, pipX, pipY, pipWidth, pipHeight);
      }
      rafRef.current = requestAnimationFrame(drawFrame);
    };
    drawFrame();

    const composedStream = canvas.captureStream(30);
    composedStreamRef.current = composedStream;
    return composedStream;
  };

  const startPreview = async () => {
    if (isLive || previewLoading) return;
    if (!shareScreenEnabled && !cameraEnabled) {
      setError("Enable screen share or camera before preview.");
      return;
    }
    setPreviewLoading(true);
    setError("");
    setNotice("");
    try {
      stopMediaStreams();
      const outputStream = await createOutputStream({
        useScreen: shareScreenEnabled,
        useCamera: cameraEnabled,
      });
      if (previewRef.current) {
        previewRef.current.srcObject = outputStream;
        previewRef.current.play().catch(() => {});
      }
      if (screenTrackRef.current) {
        screenTrackRef.current.onended = () => {
          if (!peerRef.current) {
            stopMediaStreams();
            setShowPreview(false);
            setNotice("Preview ended.");
          }
        };
      }
      setShowPreview(true);
      setNotice("Preview ready.");
    } catch (_err) {
      stopMediaStreams();
      setShowPreview(false);
      setError("Unable to start preview. Check browser permissions and try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const stopLive = async ({ endSession = true, revokeSessionAfterEnd = false, redirectToDashboard = false } = {}) => {
    const accessToken = await getAccessToken();
    clearRealtime();
    if (accessToken && id) {
      try {
        if (endSession) {
          await fetch(`${API_BASE}/api/trust-sessions/${encodeURIComponent(id)}/end`, {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        }
        if (revokeSessionAfterEnd) {
          await fetch(`${API_BASE}/api/trust-sessions/${encodeURIComponent(id)}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        }
      } catch (_err) {
        // ignore
      }
    }
    if (redirectToDashboard) {
      navigate("/dashboard");
      return;
    }
    await loadSession();
  };

  const confirmEndTrustSession = async () => {
    if (endingSession) return;
    setEndingSession(true);
    setShowEndConfirm(false);
    try {
      await stopLive({
        endSession: true,
        revokeSessionAfterEnd: true,
        redirectToDashboard: true,
      });
    } finally {
      setEndingSession(false);
    }
  };

  const startLive = async () => {
    if (!session?.id || !session?.token) return;
    if (!shareScreenEnabled && !cameraEnabled) {
      setError("Enable screen share or camera before starting.");
      return;
    }
    setStarting(true);
    setError("");
    setNotice("");
    setViewerTelemetry(null);
    setViewerPointer(null);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError("Please sign in again.");
        setStarting(false);
        return;
      }

      const startRes = await fetch(`${API_BASE}/api/trust-sessions/${encodeURIComponent(session.id)}/host-start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          includeScreen: shareScreenEnabled,
          includeCamera: cameraEnabled,
          includeSessionCode: sessionCodeEnabled,
        }),
      });
      const startPayload = await startRes.json().catch(() => ({}));
      if (!startRes.ok) {
        setError(startPayload?.error || "Unable to start trust session.");
        setStarting(false);
        return;
      }
      setSession(startPayload?.session || session);

      const previewStream =
        showPreview && previewRef.current?.srcObject && typeof previewRef.current.srcObject.getTracks === "function"
          ? previewRef.current.srcObject
          : null;
      const hasLivePreviewTrack = !!previewStream?.getVideoTracks?.().some((track) => track.readyState === "live");
      const outputStream = hasLivePreviewTrack
        ? previewStream
        : await createOutputStream({
          useScreen: shareScreenEnabled,
          useCamera: cameraEnabled,
        });

      if (previewRef.current) {
        previewRef.current.srcObject = outputStream;
        previewRef.current.play().catch(() => {});
      }
      setShowPreview(true);
      setLiveScreenEnabled(shareScreenEnabled);
      setLiveCameraEnabled(cameraEnabled);
      screenPausedRef.current = false;
      setScreenPaused(false);

      const iceServers = await resolveHostIceServers({
        sessionToken: session.token,
        accessToken,
      });
      const pc = new RTCPeerConnection({ iceServers });
      peerRef.current = pc;
      connectionEpochRef.current = makeTrustConnectionId("epoch");
      activeOfferIdRef.current = "";
      pendingRemoteCandidatesRef.current = [];
      let recentHostPollActivityUntil = Date.now() + TRUST_HOST_SIGNAL_ACTIVE_WINDOW_MS;
      let restartOfferInFlight = false;
      let lastRestartOfferAt = 0;

      const clearReconnectGraceTimer = () => {
        if (reconnectGraceTimerRef.current) {
          clearTimeout(reconnectGraceTimerRef.current);
          reconnectGraceTimerRef.current = null;
        }
      };

      const scheduleReconnectFailure = () => {
        clearReconnectGraceTimer();
        reconnectGraceTimerRef.current = setTimeout(() => {
          if (peerRef.current !== pc) return;
          if (pc.connectionState === "connected") return;
          setViewerCount(0);
          setViewerTelemetry(null);
          setViewerPointer(null);
          setError("Connection failed. Try restarting the session.");
        }, TRUST_HOST_RECONNECT_GRACE_MS);
      };

      const flushPendingRemoteCandidates = async () => {
        if (!pc.remoteDescription || pendingRemoteCandidatesRef.current.length === 0) return;
        const queued = [...pendingRemoteCandidatesRef.current];
        pendingRemoteCandidatesRef.current = [];
        for (const candidate of queued) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (_err) {
            // ignore malformed candidate
          }
        }
      };
      const senders = outputStream.getTracks().map((track) => pc.addTrack(track, outputStream));
      await Promise.all(
        senders.map((sender) =>
          applySenderQuality(sender, {
            maxBitrate: shareScreenEnabled && cameraEnabled
              ? Math.round(trustSessionLimits.maxVideoBitrate * 0.85)
              : trustSessionLimits.maxVideoBitrate,
            maxFramerate: trustSessionLimits.maxFramerate,
          })
        )
      );

      const sendRestartOffer = async ({ force = false } = {}) => {
        if (peerRef.current !== pc || restartOfferInFlight) return;
        const now = Date.now();
        if (!force && now - lastRestartOfferAt < TRUST_HOST_ICE_RESTART_DEBOUNCE_MS) return;
        restartOfferInFlight = true;
        lastRestartOfferAt = now;
        try {
          const nextOfferId = makeTrustConnectionId("offer");
          activeOfferIdRef.current = nextOfferId;
          const restartOffer = await pc.createOffer({ iceRestart: true });
          await pc.setLocalDescription(restartOffer);
          await sendHostSignal(accessToken, "offer", { sdp: pc.localDescription, offerId: nextOfferId });
        } catch (_err) {
          // Let the grace timer decide whether this becomes a terminal failure.
        } finally {
          restartOfferInFlight = false;
        }
      };

      const beginReconnectRecovery = ({ forceRestart = false } = {}) => {
        recentHostPollActivityUntil = Date.now() + TRUST_HOST_SIGNAL_ACTIVE_WINDOW_MS;
        setError("");
        setNotice("Viewer connection interrupted. Attempting to reconnect...");
        scheduleReconnectFailure();
        void sendRestartOffer({ force: forceRestart });
      };

      pc.onicecandidate = async (event) => {
        if (!event.candidate) return;
        try {
          await sendHostSignal(accessToken, "candidate", { candidate: event.candidate });
        } catch (_err) {
          // ignore
        }
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        if (state === "connected") {
          clearReconnectGraceTimer();
          recentHostPollActivityUntil = Date.now() + TRUST_HOST_SIGNAL_ACTIVE_WINDOW_MS;
          setViewerCount(1);
          setError("");
          setNotice("Viewer connected.");
        }
        if (state === "disconnected") {
          beginReconnectRecovery();
        }
        if (state === "closed") {
          clearReconnectGraceTimer();
          setViewerCount(0);
          setViewerTelemetry(null);
          setViewerPointer(null);
        }
        if (state === "failed") {
          beginReconnectRecovery({ forceRestart: true });
        }
      };

      const pollSignals = async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/trust-session/${encodeURIComponent(session.token)}/signals?role=host&cursor=${cursorRef.current}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
              cache: "no-store",
            }
          );
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) {
            return;
          }
          const signals = Array.isArray(payload?.signals) ? payload.signals : [];
          if (Number.isFinite(payload?.nextCursor)) cursorRef.current = payload.nextCursor;
          for (const signal of signals) {
            const signalType = String(signal?.signal_type || "").toLowerCase();
            if (signalType === "answer" && signal?.payload?.sdp) {
              const signalOfferId = String(signal.payload.offerId || "");
              if (signalOfferId && activeOfferIdRef.current && signalOfferId !== activeOfferIdRef.current) {
                continue;
              }
              if (!pc.currentRemoteDescription || pc.signalingState === "have-local-offer") {
                await pc.setRemoteDescription(new RTCSessionDescription(signal.payload.sdp));
                await flushPendingRemoteCandidates();
                clearReconnectGraceTimer();
                setError("");
                setNotice("Viewer connected.");
              }
            } else if (signalType === "candidate" && signal?.payload?.candidate) {
              const signalOfferId = String(signal.payload.offerId || "");
              if (signalOfferId && activeOfferIdRef.current && signalOfferId !== activeOfferIdRef.current) {
                continue;
              }
              const candidate = signal.payload.candidate;
              if (pc.remoteDescription) {
                try {
                  await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch (_err) {
                  // ignore malformed candidate
                }
              } else {
                pendingRemoteCandidatesRef.current.push(candidate);
              }
            } else if (signalType === "viewer_state" && signal?.payload) {
              recentHostPollActivityUntil = Date.now() + TRUST_HOST_SIGNAL_ACTIVE_WINDOW_MS;
              setViewerTelemetry(signal.payload);
              if (signal.payload.reconnectRequested) {
                beginReconnectRecovery({ forceRestart: true });
              }
            } else if (signalType === "viewer_pointer" && signal?.payload) {
              recentHostPollActivityUntil = Date.now() + TRUST_HOST_SIGNAL_ACTIVE_WINDOW_MS;
              setViewerPointer(signal.payload);
            } else if (signalType === "end") {
              clearReconnectGraceTimer();
              setViewerCount(0);
              setViewerTelemetry(null);
              setViewerPointer(null);
              setNotice("Viewer ended the session.");
            }
          }
        } catch (_err) {
          // retry on next poll
        }
      };

      const scheduleNextHostPoll = () => {
        if (!peerRef.current) return;
        if (pollRef.current) {
          clearTimeout(pollRef.current);
        }
        const nextDelay =
          Date.now() < recentHostPollActivityUntil
            ? TRUST_HOST_SIGNAL_FAST_POLL_MS
            : TRUST_HOST_SIGNAL_IDLE_POLL_MS;
        pollRef.current = setTimeout(async () => {
          await pollSignals();
          scheduleNextHostPoll();
        }, nextDelay);
      };

      const initialOfferId = makeTrustConnectionId("offer");
      activeOfferIdRef.current = initialOfferId;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendHostSignal(accessToken, "offer", { sdp: pc.localDescription, offerId: initialOfferId });
      setViewerCount(0);
      setNotice("Offer sent. Waiting for viewer...");
      pollSignals();
      scheduleNextHostPoll();
      setIsLive(true);
      bindLiveScreenEndedHandler(screenTrackRef.current);
    } catch (_err) {
      clearRealtime();
      setError("Unable to start live share. Check browser permissions and try again.");
    } finally {
      setStarting(false);
    }
  };

  const toggleLiveScreen = async (nextEnabled) => {
    if (!isLive) return;
    if (!nextEnabled) {
      screenPausedRef.current = false;
      setScreenPaused(false);
      frozenScreenFrameRef.current = null;
      const activeTrack = screenTrackRef.current;
      if (activeTrack) {
        manualScreenStopRef.current = true;
        try {
          activeTrack.stop();
        } catch (_err) {
          // ignore
        }
      }
      clearScreenSourceRefs({ preserveFrozenFrame: false });
      setLiveScreenEnabled(false);
      setShareScreenEnabled(false);
      setNotice("Screen share turned off.");
      return;
    }

    setError("");
    setScreenPrompting(true);
    try {
      const nextTrack = await ensureScreenSource();
      bindLiveScreenEndedHandler(nextTrack);
      setShareScreenEnabled(true);
      setLiveScreenEnabled(true);
      screenPausedRef.current = false;
      setScreenPaused(false);
      frozenScreenFrameRef.current = null;
      setNotice("Screen share resumed.");
    } catch (_err) {
      setLiveScreenEnabled(false);
      setShareScreenEnabled(false);
      setNotice("Screen share selection canceled.");
    } finally {
      setScreenPrompting(false);
    }
  };

  const toggleLiveCamera = (nextEnabled) => {
    const track = cameraTrackRef.current;
    if (!track) return;
    track.enabled = !!nextEnabled;
    setLiveCameraEnabled(track.enabled);
    setNotice(track.enabled ? "Camera resumed." : "Camera paused.");
  };

  const togglePauseScreen = () => {
    if (!screenTrackRef.current || !liveScreenEnabled) return;
    if (!screenPausedRef.current) {
      const captured = captureFrozenScreenFrame();
      if (!captured) {
        setError("Unable to pause screen right now.");
        return;
      }
      screenPausedRef.current = true;
      setScreenPaused(true);
      setNotice("Screen paused. Last frame is being shown.");
      return;
    }
    screenPausedRef.current = false;
    setScreenPaused(false);
    setNotice("Screen resumed.");
  };

  const changeLiveScreenSource = async () => {
    if (!isLive || !liveScreenEnabled) return;
    setError("");
    setScreenPrompting(true);
    const keepPausedState = screenPausedRef.current;
    try {
      const nextTrack = await ensureScreenSource({
        forcePrompt: true,
        keepPausedState,
        keepFrozenFrame: keepPausedState,
      });
      bindLiveScreenEndedHandler(nextTrack);
      setShareScreenEnabled(true);
      setLiveScreenEnabled(true);
      setNotice(keepPausedState ? "New window selected. Resume screen when ready." : "Screen share source updated.");
    } catch (_err) {
      setNotice("Screen share selection canceled.");
    } finally {
      setScreenPrompting(false);
    }
  };

  const updateShareScreenEnabled = (nextEnabled) => {
    if (!isLive && showPreview) {
      stopMediaStreams();
      setShowPreview(false);
      setNotice("");
    }
    setShareScreenEnabled(nextEnabled);
  };

  const updateCameraEnabled = (nextEnabled) => {
    if (!isLive && showPreview) {
      stopMediaStreams();
      setShowPreview(false);
      setNotice("");
    }
    setCameraEnabled(nextEnabled);
  };

  if (loading) {
    return (
      <div className="page trust-host-page">
        <div className="panel">
          <h2>Loading Trust Session...</h2>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="page trust-host-page">
        <div className="panel">
          <h2>Trust Session unavailable</h2>
          <p className="muted" style={{ marginTop: 8 }}>
            {error}
          </p>
          <div className="button-row" style={{ marginTop: 12 }}>
            <button className="ghost" type="button" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page trust-host-page">
      <div className="panel trust-host-panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">Trust Session Host Studio</p>
            <h2>Live verification share</h2>
          </div>
          <span className={`badge ${isLive ? "success" : "muted"}`}>
            {isLive ? `Live (${viewerCount} ${viewerCount === 1 ? "viewer" : "viewers"})` : "Not live"}
          </span>
        </div>
        {!isLive && (
          <p className="muted">
            Configure sources and launch your broadcast. Recipient always receives view-only stream.
          </p>
        )}
        {!isLive && (
          <div className="trust-switch-list" style={{ marginTop: 12 }}>
            <label className="trust-switch-row">
              <span>Share screen</span>
              <span className="trust-switch">
                <input
                  type="checkbox"
                  checked={shareScreenEnabled}
                  onChange={(e) => updateShareScreenEnabled(e.target.checked)}
                />
                <span className="trust-switch-slider" />
              </span>
            </label>
            <label className="trust-switch-row">
              <span>Share camera</span>
              <span className="trust-switch">
                <input
                  type="checkbox"
                  checked={cameraEnabled}
                  onChange={(e) => updateCameraEnabled(e.target.checked)}
                />
                <span className="trust-switch-slider" />
              </span>
            </label>
            <label className="trust-switch-row">
              <span>Session code</span>
              <span className="trust-switch">
                <input
                  type="checkbox"
                  checked={sessionCodeEnabled}
                  onChange={(e) => setSessionCodeEnabled(e.target.checked)}
                />
                <span className="trust-switch-slider" />
              </span>
            </label>
          </div>
        )}
        <div className="trust-host-share-row">
          {isLive ? (
            <>
              <input className="trust-host-link-input" type="text" value={session?.session_url || ""} readOnly />
              <button className="ghost" type="button" onClick={copyLink}>
                {copied ? "Copied" : "Copy Link"}
              </button>
              <button
                className={`ghost trust-sms-action${!TRUST_SESSION_SMS_ENABLED ? " is-disabled" : ""}`}
                type="button"
                onClick={openSmsModal}
                disabled={!TRUST_SESSION_SMS_ENABLED || sendingSms}
              >
                <span>Send SMS</span>
                {!TRUST_SESSION_SMS_ENABLED && <span className="trust-coming-soon-tag">Coming soon</span>}
              </button>
              <button className="ghost" type="button" onClick={openEmailModal} disabled={sendingEmail}>
                Send Email
              </button>
            </>
          ) : (
            <span className="muted small">Start Trust Session to reveal link and sharing options.</span>
          )}
        </div>

        <div className="trust-host-preview">
          <video
            ref={previewRef}
            autoPlay
            muted
            playsInline
            className={`trust-video ${showPreview || isLive ? "" : "trust-video-hidden"}`}
          />
          {showViewerActivityOverlay && (
            <div className="trust-viewer-overlay-layer" aria-hidden="true">
              {viewerTelemetryReady ? (
                <>
                  <div className="trust-viewer-overlay-hud">
                    <span className={`trust-viewer-presence-badge ${viewerAwayFromSession ? "is-away" : "is-active"}`}>
                      {viewerAwayFromSession ? "Viewer away from Trust Session" : "Viewer on Trust Session"}
                    </span>
                  </div>
                  {viewerHasVisibleFrame && (
                    <div
                      className="trust-viewer-visible-frame"
                      style={{
                        left: `${viewerVisibleFrame.x * 100}%`,
                        top: `${viewerVisibleFrame.y * 100}%`,
                        width: `${viewerVisibleFrame.width * 100}%`,
                        height: `${viewerVisibleFrame.height * 100}%`,
                      }}
                    />
                  )}
                  {showViewerPointer && (
                    <div
                      className="trust-viewer-pointer"
                      style={{
                        left: `${viewerPointerFrame.x * 100}%`,
                        top: `${viewerPointerFrame.y * 100}%`,
                      }}
                    >
                      {showViewerPointerPulse && (
                        <span
                          key={`pulse-${viewerPointerFrame.sequence}`}
                          className="trust-viewer-pointer-pulse"
                        />
                      )}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" />
                      </svg>
                    </div>
                  )}
                  {viewerAwayFromSession && (
                    <div className="trust-viewer-away-modal">
                      <strong>Viewer is away from Trust Session</strong>
                      <span>The viewer has switched tabs or apps and is not viewing the Trust Session window</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="trust-viewer-pending-modal">
                  <strong>Viewer context unavailable.</strong>
                  <span>Viewer is connected, but Host Studio has not received viewport telemetry yet.</span>
                </div>
              )}
            </div>
          )}
          {!isLive && !showPreview && (
            <div className="trust-preview-overlay">
              <button
                type="button"
                className="primary"
                onClick={startPreview}
                disabled={previewLoading || starting}
              >
                {previewLoading ? "Loading Preview..." : "Show Preview"}
              </button>
            </div>
          )}
        </div>

        {error && <p className="error-message">{error}</p>}

        {isLive && (
          <div className="trust-switch-list" style={{ marginTop: 12 }}>
            <label className="trust-switch-row">
              <span>Screen share live</span>
              <span className="trust-switch">
                <input
                  type="checkbox"
                  checked={liveScreenEnabled}
                  onChange={(e) => {
                    void toggleLiveScreen(e.target.checked);
                  }}
                  disabled={screenPrompting}
                />
                <span className="trust-switch-slider" />
              </span>
            </label>
            {cameraTrackRef.current && (
              <label className="trust-switch-row">
                <span>Camera live</span>
                <span className="trust-switch">
                  <input
                    type="checkbox"
                    checked={liveCameraEnabled}
                    onChange={(e) => toggleLiveCamera(e.target.checked)}
                  />
                  <span className="trust-switch-slider" />
                </span>
              </label>
            )}
            {liveScreenEnabled && (
              <>
                <button
                  type="button"
                  className={`ghost trust-screen-pause-btn ${screenPaused ? "is-resume" : "is-pause"}`}
                  onClick={togglePauseScreen}
                  disabled={!screenTrackRef.current || screenPrompting}
                >
                  {screenPaused ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
                      </svg>
                      <span>Resume screen</span>
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="14" y="3" width="5" height="18" rx="1" />
                        <rect x="5" y="3" width="5" height="18" rx="1" />
                      </svg>
                      <span>Pause screen</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="ghost trust-screen-change-btn"
                  onClick={() => {
                    void changeLiveScreenSource();
                  }}
                  disabled={screenPrompting}
                >
                  {screenPrompting ? "Choosing window..." : "Change window"}
                </button>
              </>
            )}
          </div>
        )}

        <div className="trust-host-actions">
          <div className="trust-host-actions-left">
            <button className="ghost" type="button" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </button>
          </div>
          <div className="trust-host-actions-right">
            {!isLive ? (
              <button className="primary" type="button" onClick={startLive} disabled={starting}>
                {starting ? "Starting..." : "Start Trust Session"}
              </button>
            ) : (
              <button className="danger" type="button" onClick={() => setShowEndConfirm(true)} disabled={endingSession}>
                {endingSession ? "Ending..." : "End Trust Session"}
              </button>
            )}
          </div>
        </div>
      </div>
      {showEndConfirm && (
        <div className="trust-end-modal-backdrop" onClick={() => setShowEndConfirm(false)}>
          <div
            className="trust-end-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trust-end-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="trust-end-modal-header">
              <h3 id="trust-end-modal-title">End Trust Session?</h3>
              <button
                type="button"
                className="trust-end-modal-close"
                onClick={() => setShowEndConfirm(false)}
                aria-label="Close end session confirmation"
              >
                x
              </button>
            </div>
            <div className="trust-end-modal-body">
              <p className="muted" style={{ margin: 0 }}>
                This will end the current Trust Session immediately. You cannot restart this same session.
              </p>
              <div className="button-row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
                <button type="button" className="ghost" onClick={() => setShowEndConfirm(false)} disabled={endingSession}>
                  Cancel
                </button>
                <button type="button" className="danger" onClick={confirmEndTrustSession} disabled={endingSession}>
                  {endingSession ? "Ending..." : "End Trust Session"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSmsModal && (
        <div className="trust-email-modal-backdrop" onClick={closeSmsModal}>
          <div
            className="trust-email-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trust-sms-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="trust-email-modal-header">
              <h3 id="trust-sms-modal-title">Send Trust Session SMS</h3>
              <button
                type="button"
                className="trust-email-modal-close"
                onClick={closeSmsModal}
                aria-label="Close SMS modal"
              >
                x
              </button>
            </div>
            <form className="trust-email-modal-body" onSubmit={continueToSmsConsent}>
              <label htmlFor="trust-sms-recipient">Recipient phone</label>
              <input
                id="trust-sms-recipient"
                type="tel"
                className="trust-email-input"
                placeholder="(469) 555-1234 or +14695551234"
                value={smsRecipient}
                onChange={(event) => setSmsRecipient(event.target.value)}
                disabled={sendingSms}
                autoFocus
              />

              {smsModalStatus === "success" && (
                <div className="trust-email-feedback success" role="status" aria-live="polite">
                  <span className="trust-email-feedback-icon" aria-hidden="true">✓</span>
                  <span>{smsModalMessage}</span>
                </div>
              )}
              {smsModalStatus === "error" && (
                <div className="trust-email-feedback error" role="alert">
                  <span className="trust-email-feedback-icon" aria-hidden="true">x</span>
                  <span>{smsModalMessage}</span>
                </div>
              )}

              <div className="trust-sms-entry-actions">
                <button type="button" className="ghost" onClick={closeSmsModal} disabled={sendingSms}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary"
                  disabled={sendingSms}
                >
                  {sendingSms ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showSmsConsentModal && (
        <div className="trust-email-modal-backdrop" onClick={closeSmsModal}>
          <div
            className="trust-email-modal trust-sms-consent-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trust-sms-consent-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="trust-email-modal-header">
              <h3 id="trust-sms-consent-title">Confirm Consent for Sending Message</h3>
              <button
                type="button"
                className="trust-email-modal-close"
                onClick={closeSmsModal}
                aria-label="Close SMS consent modal"
              >
                x
              </button>
            </div>
            <form className="trust-email-modal-body" onSubmit={sendSmsLink}>
              <div className="trust-sms-consent-script-block" aria-label="Verbal consent script">
                {TRUST_SESSION_SMS_VERBAL_CONSENT_SCRIPT}
              </div>
              <label htmlFor="trust-sms-consent" className="trust-sms-consent-row">
                <span className="trust-sms-checkbox">
                  <input
                    id="trust-sms-consent"
                    type="checkbox"
                    checked={smsConsentConfirmed}
                    onChange={(event) => setSmsConsentConfirmed(event.target.checked)}
                    disabled={sendingSms || smsModalStatus === "success"}
                  />
                  <span className="trust-sms-checkbox-box" aria-hidden="true" />
                </span>
                <span className="trust-sms-consent-copy">
                  {TRUST_SESSION_SMS_CONSENT_CONFIRMATION_LABEL}
                </span>
              </label>

              {smsModalStatus === "success" && (
                <div className="trust-email-feedback success" role="status" aria-live="polite">
                  <span className="trust-email-feedback-icon" aria-hidden="true">✓</span>
                  <span>{smsModalMessage}</span>
                </div>
              )}
              {smsModalStatus === "error" && (
                <div className="trust-email-feedback error" role="alert">
                  <span className="trust-email-feedback-icon" aria-hidden="true">x</span>
                  <span>{smsModalMessage}</span>
                </div>
              )}

              <div className="trust-sms-consent-actions">
                <button type="button" className="ghost" onClick={closeSmsModal} disabled={sendingSms}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary"
                  disabled={sendingSms || smsModalStatus === "success" || !smsConsentConfirmed}
                >
                  {sendingSms ? "Sending..." : "Confirm and Send SMS"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEmailModal && (
        <div className="trust-email-modal-backdrop" onClick={closeEmailModal}>
          <div
            className="trust-email-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trust-email-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="trust-email-modal-header">
              <h3 id="trust-email-modal-title">Send Trust Session Email</h3>
              <button
                type="button"
                className="trust-email-modal-close"
                onClick={closeEmailModal}
                aria-label="Close email modal"
              >
                x
              </button>
            </div>
            <form className="trust-email-modal-body" onSubmit={sendEmailLink}>
              <label htmlFor="trust-email-recipient">Recipient email</label>
              <input
                id="trust-email-recipient"
                type="email"
                className="trust-email-input"
                placeholder="name@example.com"
                value={emailRecipient}
                onChange={(event) => setEmailRecipient(event.target.value)}
                disabled={sendingEmail || emailModalStatus === "success"}
                autoFocus
              />

              {emailModalStatus === "success" && (
                <div className="trust-email-feedback success" role="status" aria-live="polite">
                  <span className="trust-email-feedback-icon" aria-hidden="true">✓</span>
                  <span>{emailModalMessage}</span>
                </div>
              )}
              {emailModalStatus === "error" && (
                <div className="trust-email-feedback error" role="alert">
                  <span className="trust-email-feedback-icon" aria-hidden="true">x</span>
                  <span>{emailModalMessage}</span>
                </div>
              )}

              <div className="button-row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
                <button type="button" className="ghost" onClick={closeEmailModal} disabled={sendingEmail}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={sendingEmail || emailModalStatus === "success"}>
                  {sendingEmail ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
